import React, { useEffect, useState, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { cartContext } from './CartContext';

const RecentlyViewed = () => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { addItemToCart } = useContext(cartContext);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const viewedProducts = JSON.parse(localStorage.getItem('recentlyViewed')) || [];
    
    // If no products in localStorage, use sample data for demonstration
    if (viewedProducts.length === 0) {
      const sampleProducts = [
        {
          "id": "e2749162-3568-44b8-8d82-bd2e47f53761",
          "name": "Lenovo ThinkPad T450s",
          "image": "[\"https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/a72084db-d7b2-4448-97ae-6d2449710c84.webp\",\"https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/fc93e88b-76a2-455b-9ba8-ca4e395628b8.webp\"]",
          "price": 16500,
          "rating": 4.3,
          "reviews": 31088,
          "inStock": true
        },
        {
          "id": "sample-2",
          "name": "Samsung Galaxy Tab A8",
          "image": "[\"https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/tablet-sample1.webp\"]",
          "price": 25000,
          "rating": 4.5,
          "reviews": 15420,
          "inStock": true
        },
        {
          "id": "sample-3",
          "name": "HP Pavilion 15 Laptop",
          "image": "[\"https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/laptop-sample2.webp\"]",
          "price": 45000,
          "rating": 4.2,
          "reviews": 8956,
          "inStock": true
        },
        {
          "id": "sample-4",
          "name": "iPhone 13 Pro Max",
          "image": "[\"https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/iphone-sample.webp\"]",
          "price": 89000,
          "rating": 4.8,
          "reviews": 24680,
          "inStock": true
        }
      ];
      setRecentlyViewed(sampleProducts);
    } else {
      setRecentlyViewed(viewedProducts.slice(0, 8)); // Limit to 8 items for better display
    }
    
    console.log('Recently viewed products loaded');
  }, []);

  const getProductImageUrl = (imageData) => {
    if (!imageData) return '/api/placeholder/400/400';
    
    // Handle JSON string containing array of URLs
    if (typeof imageData === 'string') {
      try {
        const imageArray = JSON.parse(imageData);
        if (Array.isArray(imageArray) && imageArray.length > 0) {
          return imageArray[0]; // Return first image from array
        }
      } catch (e) {
        // If parsing fails, treat as single URL
        if (imageData.includes('http')) return imageData;
        return `https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/${imageData}`;
      }
    }
    
    // Handle array directly
    if (Array.isArray(imageData) && imageData.length > 0) {
      return imageData[0];
    }
    
    // Handle single URL
    if (imageData.includes && imageData.includes('http')) return imageData;
    return `https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/${imageData}`;
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const cartItem = {
      productId: product.productId || product.productID || product.id,
      productName: product.productName || product.name,
      price: product.price,
      quantity: 1,
      productImage: product.productImage || product.image,
      inStock: product.inStock !== false
    };
    
    addItemToCart(cartItem);
  };

  // Enhanced ProductCard component matching the main page design
  const RecentProductCard = ({ product }) => {
    const discount = (product.discount/100) || 0;
    const discountedPrice = (product.price * (1 - discount)).toFixed(2);
    const firstImage = getProductImageUrl(product.image || product.imageUrl || product.productImage);
    const productId = product.productId || product.productID || product.id;
    const productName = product.productName || product.name;
  
    return (
      <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group overflow-hidden border border-gray-100 hover:border-gray-200 h-full flex flex-col">
        {discount > 0 && (
          <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md">
            -{(discount * 100).toFixed(0)}% OFF
          </div>
        )}
        
        <Link to={`/product/${encodeURIComponent(productName)}/${productId}`} className="block h-full flex flex-col">
          {/* Product Image */}
          <div className="relative overflow-hidden bg-gray-50 flex-shrink-0">
            <img 
              src={firstImage} 
              alt={productName}
              className="w-full h-32 sm:h-40 md:h-48 object-contain group-hover:scale-105 transition-transform duration-300 p-3" 
              onError={(e) => {
                e.target.src = '/api/placeholder/400/400';
              }}
            />
          </div>
          
          {/* Product Details */}
          <div className="p-3 space-y-2 flex-grow flex flex-col">
            {/* Product Name */}
            <h3 className="text-xs sm:text-sm font-medium text-gray-800 hover:text-blue-600 transition-colors line-clamp-2 leading-relaxed flex-shrink-0">
              {productName}
            </h3>
            
            {/* Stock Status */}
            <div className={`text-xs font-medium flex-shrink-0 ${
              product.inStock !== false
                ? 'text-green-600' 
                : 'text-red-500'
            }`}>
              {product.inStock !== false
                ? `✓ In Stock${product.stockQuantity ? ` (${product.stockQuantity})` : ''}` 
                : "⚠ Out of Stock"
              }
            </div>
            
            {/* Rating */}
            <div className="flex items-center space-x-1 text-xs flex-shrink-0">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={i < (product.rating || 4) ? "text-yellow-400" : "text-gray-300"}>★</span>
                ))}
              </div>
              <span className="text-gray-600">{product.rating || '4.0'}</span>
              <span className="text-gray-400">({product.reviews || Math.floor(Math.random() * 500 + 50)})</span>
            </div>
            
            {/* Spacer */}
            <div className="flex-grow"></div>
            
            {/* Price Section */}
            <div className="space-y-1 flex-shrink-0 mt-auto">
              {discount > 0 && (
                <div className="text-xs text-gray-400 line-through">
                  KSH {Number(product.price).toLocaleString()}
                </div>
              )}
              <div className="text-sm sm:text-base font-bold text-gray-900">
                KSH {discount > 0 ? Number(discountedPrice).toLocaleString() : Number(product.price).toLocaleString()}
              </div>
            </div>
          </div>
        </Link>
        
        {/* Add to Cart Button */}
        <div className="p-3 pt-0">
          <button 
            onClick={(e) => handleAddToCart(product, e)}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-4 text-xs sm:text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={product.inStock === false}
          >
            {product.inStock === false ? 'Out of Stock' : 'Add to Cart'}
          </button>
        </div>
      </div>
    );
  };
  
  if (recentlyViewed.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Recently Viewed</h2>
        <div className="text-center py-8">
          <div className="text-gray-400 mb-3">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">No recently viewed products</p>
          <p className="text-gray-400 text-xs mt-1">Start browsing to see your recently viewed items here</p>
          <Link 
            to="/" 
            className="inline-block mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Recently Viewed</h2>
          {recentlyViewed.length > 4 && (
            <button 
              onClick={() => {
                // Could navigate to a full recently viewed page
                console.log('View all recently viewed');
              }}
              className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors"
            >
              View All
            </button>
          )}
        </div>
      </div>
      
      {/* Products Horizontal Scroll */}
      <div className="p-4 sm:p-6">
        <div className="relative group">
          {/* Scroll Left Button */}
          {recentlyViewed.length > 4 && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white text-gray-600 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg hover:bg-gray-50 border border-gray-200"
            >
              <FaChevronLeft className="h-4 w-4" />
            </button>
          )}
          
          {/* Scroll Right Button */}
          {recentlyViewed.length > 4 && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white text-gray-600 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg hover:bg-gray-50 border border-gray-200"
            >
              <FaChevronRight className="h-4 w-4" />
            </button>
          )}
          
          {/* Horizontal Scroll Container */}
          <div 
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto py-2 scroll-smooth snap-x snap-mandatory no-scrollbar"
          >
            {recentlyViewed.map((product, index) => (
              <div key={`${product.productId || product.productID || product.id}-${index}`} className="flex-shrink-0 w-52 snap-start">
                <RecentProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewed;
